/**
 * Scope 3 Data Collection Page
 * Comprehensive control center for managing Scope 3 emissions data collection.
 * 
 * Features:
 * - Dashboard overview of all 15 Scope 3 categories
 * - Project management with 5-stage workflow
 * - Multiple collection methods (supplier survey, manual, spend-based)
 * - PCAF data quality scoring
 * - Integration with Supplier Portal
 * - Three Superpowers display (Cross-Framework, Ownership, Defensibility)
 */
import React, { useState, useEffect } from 'react';
import { 
  Layers, BarChart3, AlertTriangle, CheckCircle2, Clock, Filter, Download, TrendingUp,
  Plus, ChevronRight, ArrowUpRight, ArrowDownRight, Target, Users, FileText, Settings,
  Building2, Factory, Truck, Plane, Trash2, Briefcase, Home, Package, ShoppingCart,
  Zap, Leaf, Shield, Link2, User, Activity, AlertCircle, Info, X, RefreshCw,
  Send, Upload, Calculator, Database
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { JourneySection, JourneyPanel } from '../journeys/JourneyPanel';
import { Scope3Estimator, SupplierCampaigns, CSDDDScreening } from '../journeys/JourneyValueChain';
import { apiFetch } from '../../services/api_service';


const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Category Icons
const CATEGORY_ICONS = {
  1: ShoppingCart,  // Purchased Goods
  2: Building2,     // Capital Goods
  3: Zap,           // Fuel & Energy
  4: Truck,         // Upstream Transport
  5: Trash2,        // Waste
  6: Plane,         // Business Travel
  7: Home,          // Employee Commuting
  8: Building2,     // Upstream Leased
  9: Truck,         // Downstream Transport
  10: Factory,      // Processing
  11: Package,      // Use of Products
  12: Trash2,       // End of Life
  13: Building2,    // Downstream Leased
  14: Building2,    // Franchises
  15: Briefcase,    // Investments
};

// Status Colors
const STATUS_CONFIG = {
  complete: { label: 'Complete', bg: 'bg-emerald-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30', icon: CheckCircle2 },
  in_progress: { label: 'In Progress', bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30', icon: Clock },
  not_started: { label: 'Not Started', bg: 'bg-gray-500/20', text: 'text-gray-400', border: 'border-gray-500/30', icon: AlertCircle },
  gap: { label: 'Gap', bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30', icon: AlertTriangle },
};

// PCAF Quality Colors
const PCAF_COLORS = {
  5: { bg: 'bg-emerald-500', text: 'text-emerald-400', label: 'Audited' },
  4: { bg: 'bg-teal-500', text: 'text-teal-400', label: 'Primary' },
  3: { bg: 'bg-blue-500', text: 'text-blue-400', label: 'Modeled' },
  2: { bg: 'bg-amber-500', text: 'text-amber-400', label: 'Economic' },
  1: { bg: 'bg-red-500', text: 'text-red-400', label: 'Estimated' },
};

// Collection Method Info
const COLLECTION_METHODS = {
  supplier_survey: { icon: Users, label: 'Supplier Survey', desc: 'Request data from suppliers', quality: '3-5' },
  manual_upload: { icon: Upload, label: 'Manual Upload', desc: 'Upload spreadsheets/docs', quality: '2-4' },
  spend_based: { icon: Calculator, label: 'Spend-Based', desc: 'Calculate from procurement', quality: '1-2' },
  activity_based: { icon: Activity, label: 'Activity-Based', desc: 'From activity data', quality: '3-4' },
  employee_survey: { icon: Users, label: 'Employee Survey', desc: 'Survey employees', quality: '2-3' },
};

export default function Scope3DataCollectionPage() {
  const [dashboard, setDashboard] = useState(null);
  const [projects, setProjects] = useState([]);
  const [collectionMethods, setCollectionMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showCreateProject, setShowCreateProject] = useState(false);
  const [reportingYear] = useState(2025);

  // Fetch dashboard data
  useEffect(() => {
    fetchDashboard();
    fetchProjects();
    fetchCollectionMethods();
  }, [reportingYear]);

  const fetchDashboard = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/scope3/dashboard?reportingYear=${reportingYear}`);
      const data = await res.json();
      setDashboard(data);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      toast.error('Failed to load Scope 3 dashboard');
    }
  };

  const fetchProjects = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/scope3/projects?reportingYear=${reportingYear}`);
      const data = await res.json();
      setProjects(data.projects || []);
    } catch (err) {
      console.error('Error fetching projects:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCollectionMethods = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/scope3/collection-methods`);
      const data = await res.json();
      setCollectionMethods(data.methods || []);
    } catch (err) {
      console.error('Error fetching methods:', err);
    }
  };

  const createProject = async (categoryId, name) => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/scope3/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          categoryId,
          reportingYear,
          name,
          description: `Scope 3 Cat ${categoryId} data collection for ${reportingYear}`
        })
      });
      const project = await res.json();
      toast.success(`Project created: ${project.projectId}`);
      fetchProjects();
      fetchDashboard();
      setShowCreateProject(false);
      return project;
    } catch (err) {
      console.error('Error creating project:', err);
      toast.error('Failed to create project');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <RefreshCw className="animate-spin text-emerald-400" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="scope3-collection-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="p-2 rounded-lg bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/30">
              <Layers className="text-emerald-400" size={24} />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Scope 3 Data Collection</h1>
              <p className="text-sm text-[#6B6B6B]">
                Control center for value chain emissions • {reportingYear} Reporting Year
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => { fetchDashboard(); fetchProjects(); }}
            className="border-[#2F2F2F] text-[#A0A0A0] hover:text-white"
          >
            <RefreshCw size={14} className="mr-2" />
            Refresh
          </Button>
          <Button 
            onClick={() => setShowCreateProject(true)}
            className="bg-emerald-500 hover:bg-emerald-600 text-black"
            data-testid="create-project-btn"
          >
            <Plus size={16} className="mr-2" />
            New Project
          </Button>
        </div>
      </div>

      {/* Three Superpowers Banner */}
      <div className="grid grid-cols-3 gap-4">
        <SuperpowerCard
          icon={Link2}
          title="Cross-Framework Intelligence"
          description="Same data satisfies CSRD, CDP, GRI, TCFD simultaneously"
          color="teal"
          value="4x"
          label="Efficiency"
        />
        <SuperpowerCard
          icon={User}
          title="Ownership & Accountability"
          description="Every data point has clear owner and approval chain"
          color="blue"
          value="100%"
          label="Traceable"
        />
        <SuperpowerCard
          icon={Shield}
          title="Audit Defensibility"
          description="Complete evidence trail from collection to disclosure"
          color="purple"
          value="SHA-256"
          label="Verified"
        />
      </div>

      {/* Summary Stats */}
      {dashboard && (
        <div className="grid grid-cols-5 gap-4">
          <StatCard
            icon={Layers}
            value={formatNumber(dashboard.totalEmissions_tCO2e)}
            unit="tCO2e"
            label="Total Scope 3"
            color="purple"
          />
          <StatCard
            icon={Target}
            value={`${dashboard.averageCoverage?.toFixed(0) || 0}%`}
            label="Avg Coverage"
            color="blue"
            showProgress
            progress={dashboard.averageCoverage || 0}
          />
          <StatCard
            icon={Activity}
            value={dashboard.averageDataQuality?.toFixed(1) || '0'}
            unit="/5 PCAF"
            label="Data Quality"
            color={dashboard.averageDataQuality >= 3 ? 'emerald' : 'amber'}
          />
          <StatCard
            icon={CheckCircle2}
            value={dashboard.categoriesComplete || 0}
            unit={`/${dashboard.totalCategories || 15}`}
            label="Categories Done"
            color="emerald"
          />
          <StatCard
            icon={AlertTriangle}
            value={dashboard.categoriesWithGaps || 0}
            label="Critical Gaps"
            color="red"
          />
        </div>
      )}

      {/* Categories Grid */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">Scope 3 Categories</h2>
          <div className="flex items-center gap-2 text-xs text-[#6B6B6B]">
            <span className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-emerald-500" /> Complete
            </span>
            <span className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-amber-500" /> In Progress
            </span>
            <span className="flex items-center gap-1">
              <div className="w-2 h-2 rounded-full bg-red-500" /> Gap
            </span>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          {dashboard?.categories?.map((category) => (
            <CategoryCard
              key={category.categoryId}
              category={category}
              projects={projects.filter(p => p.categoryId === category.categoryId)}
              onSelect={() => setSelectedCategory(category)}
              onCreateProject={(name) => createProject(category.categoryId, name)}
            />
          ))}
        </div>
      </div>

      {/* Active Projects */}
      {projects.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Active Projects</h2>
          <div className="grid grid-cols-2 gap-4">
            {projects.slice(0, 4).map((project) => (
              <ProjectCard key={project.projectId} project={project} />
            ))}
          </div>
        </div>
      )}

      {/* Category Detail Modal */}
      {selectedCategory && (
        <CategoryDetailModal
          category={selectedCategory}
          projects={projects.filter(p => p.categoryId === selectedCategory.categoryId)}
          collectionMethods={collectionMethods}
          onClose={() => setSelectedCategory(null)}
          onCreateProject={(name) => createProject(selectedCategory.categoryId, name)}
        />
      )}

      {/* Create Project Modal */}
      {showCreateProject && (
        <CreateProjectModal
          categories={dashboard?.categories || []}
          onClose={() => setShowCreateProject(false)}
          onCreateProject={createProject}
        />
      )}

      {/* Live User Journeys */}
      <JourneySection title="Live Journeys">
        <JourneyPanel title="Spend-Based Scope 3 Estimator" ucId="UC-06"><Scope3Estimator /></JourneyPanel>
        <JourneyPanel title="Supplier Campaigns" ucId="UC-07"><SupplierCampaigns /></JourneyPanel>
        <JourneyPanel title="CSDDD Screening" ucId="UC-21"><CSDDDScreening /></JourneyPanel>
      </JourneySection>
    </div>
  );
}

// ============================================================================
// COMPONENT: Superpower Card
// ============================================================================
function SuperpowerCard({ icon: Icon, title, description, color, value, label }) {
  const colors = {
    teal: 'from-teal-500/10 to-teal-500/5 border-teal-500/20',
    blue: 'from-blue-500/10 to-blue-500/5 border-blue-500/20',
    purple: 'from-purple-500/10 to-purple-500/5 border-purple-500/20',
  };
  const iconColors = {
    teal: 'text-teal-400',
    blue: 'text-blue-400',
    purple: 'text-purple-400',
  };

  return (
    <div className={`bg-gradient-to-br ${colors[color]} border rounded-xl p-4`}>
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <Icon className={iconColors[color]} size={20} />
          <div>
            <h3 className="text-sm font-semibold text-white">{title}</h3>
            <p className="text-xs text-[#6B6B6B] mt-1">{description}</p>
          </div>
        </div>
        <div className="text-right">
          <div className={`text-lg font-bold ${iconColors[color]}`}>{value}</div>
          <div className="text-[10px] text-[#6B6B6B]">{label}</div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// COMPONENT: Stat Card
// ============================================================================
function StatCard({ icon: Icon, value, unit, label, color, showProgress, progress }) {
  const colors = {
    purple: 'bg-purple-500/20 text-purple-400',
    blue: 'bg-blue-500/20 text-blue-400',
    emerald: 'bg-emerald-500/20 text-emerald-400',
    amber: 'bg-amber-500/20 text-amber-400',
    red: 'bg-red-500/20 text-red-400',
  };

  return (
    <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colors[color]}`}>
          <Icon size={18} />
        </div>
        <div className="flex-1">
          <div className="flex items-baseline gap-1">
            <span className="text-xl font-bold text-white">{value}</span>
            {unit && <span className="text-xs text-[#6B6B6B]">{unit}</span>}
          </div>
          <div className="text-xs text-[#6B6B6B]">{label}</div>
          {showProgress && (
            <Progress value={progress} className="h-1 mt-2" />
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// COMPONENT: Category Card
// ============================================================================
function CategoryCard({ category, projects, onSelect, onCreateProject }) {
  const Icon = CATEGORY_ICONS[category.categoryId] || Package;
  const statusConfig = STATUS_CONFIG[category.status] || STATUS_CONFIG.gap;
  const StatusIcon = statusConfig.icon;
  const hasProject = projects.length > 0;

  return (
    <div 
      className={`bg-[#111111] border ${statusConfig.border} rounded-xl p-4 hover:bg-[#151515] transition-all cursor-pointer group`}
      onClick={onSelect}
      data-testid={`scope3-category-${category.categoryId}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`p-2 rounded-lg ${statusConfig.bg}`}>
            <Icon size={16} className={statusConfig.text} />
          </div>
          <div>
            <div className="text-xs font-mono text-emerald-400">Cat {category.categoryId}</div>
            <div className="text-sm font-medium text-white line-clamp-1">{category.name}</div>
          </div>
        </div>
        <ChevronRight size={16} className="text-[#3F3F3F] group-hover:text-white transition-colors" />
      </div>

      {/* Emissions & Coverage */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs">
          <span className="text-[#6B6B6B]">Emissions</span>
          <span className="font-mono text-white">
            {category.emissions_tCO2e > 0 
              ? `${formatNumber(category.emissions_tCO2e)} tCO2e`
              : '—'
            }
          </span>
        </div>
        
        <div className="space-y-1">
          <div className="flex items-center justify-between text-xs">
            <span className="text-[#6B6B6B]">Coverage</span>
            <span className={`font-medium ${
              category.coveragePercent >= 80 ? 'text-emerald-400' : 
              category.coveragePercent >= 50 ? 'text-amber-400' : 'text-red-400'
            }`}>
              {category.coveragePercent?.toFixed(0) || 0}%
            </span>
          </div>
          <div className="h-1.5 bg-[#1F1F1F] rounded-full overflow-hidden">
            <div 
              className={`h-full rounded-full transition-all ${
                category.coveragePercent >= 80 ? 'bg-emerald-500' : 
                category.coveragePercent >= 50 ? 'bg-amber-500' : 'bg-red-500'
              }`}
              style={{ width: `${category.coveragePercent || 0}%` }}
            />
          </div>
        </div>

        {/* Data Quality */}
        {category.dataQualityScore > 0 && (
          <div className="flex items-center justify-between text-xs">
            <span className="text-[#6B6B6B]">Quality</span>
            <div className="flex items-center gap-1">
              <span className={`font-medium ${PCAF_COLORS[Math.round(category.dataQualityScore)]?.text || 'text-gray-400'}`}>
                {category.dataQualityScore.toFixed(1)}/5
              </span>
              <span className="text-[#3F3F3F]">PCAF</span>
            </div>
          </div>
        )}
      </div>

      {/* Status Badge */}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#1F1F1F]">
        <Badge className={`${statusConfig.bg} ${statusConfig.text} border-0 text-[10px]`}>
          {StatusIcon && <StatusIcon size={10} className="mr-1" />}
          {statusConfig.label}
        </Badge>
        {category.upstream !== undefined && (
          <span className="text-[10px] text-[#3F3F3F]">
            {category.upstream ? 'Upstream' : 'Downstream'}
          </span>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// COMPONENT: Project Card
// ============================================================================
function ProjectCard({ project }) {
  const stages = ['scoping', 'collection', 'calculation', 'validation', 'finalization'];
  const currentStageIndex = stages.indexOf(project.stage);

  return (
    <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="text-xs font-mono text-emerald-400">{project.projectId}</div>
          <div className="text-sm font-medium text-white mt-0.5">{project.name}</div>
        </div>
        <Badge className="bg-amber-500/20 text-amber-400 border-0 text-[10px]">
          {project.stage?.replace('_', ' ')}
        </Badge>
      </div>

      {/* Stage Progress */}
      <div className="flex items-center gap-1 mb-3">
        {stages.map((stage, i) => (
          <div 
            key={stage}
            className={`flex-1 h-1 rounded-full ${
              i <= currentStageIndex ? 'bg-emerald-500' : 'bg-[#1F1F1F]'
            }`}
          />
        ))}
      </div>

      {/* Results */}
      {project.results && (
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <div className="text-sm font-bold text-white">
              {formatNumber(project.results.totalEmissions_tCO2e || 0)}
            </div>
            <div className="text-[10px] text-[#6B6B6B]">tCO2e</div>
          </div>
          <div>
            <div className="text-sm font-bold text-white">
              {project.results.coveragePercent?.toFixed(0) || 0}%
            </div>
            <div className="text-[10px] text-[#6B6B6B]">Coverage</div>
          </div>
          <div>
            <div className="text-sm font-bold text-white">
              {project.results.dataQualityScore?.toFixed(1) || '—'}
            </div>
            <div className="text-[10px] text-[#6B6B6B]">Quality</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// COMPONENT: Category Detail Modal
// ============================================================================
function CategoryDetailModal({ category, projects, collectionMethods, onClose, onCreateProject }) {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [projectName, setProjectName] = useState(`Cat ${category.categoryId}: ${category.name} - 2025`);
  const Icon = CATEGORY_ICONS[category.categoryId] || Package;

  const applicableMethods = collectionMethods.filter(m => 
    m.applicableCategories?.includes(category.categoryId)
  );

  const handleCreate = () => {
    onCreateProject(projectName);
    setShowCreateForm(false);
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[#0A0A0A] border border-[#1F1F1F] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[#1F1F1F]">
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-xl bg-emerald-500/20">
              <Icon className="text-emerald-400" size={24} />
            </div>
            <div>
              <div className="text-xs font-mono text-emerald-400">Category {category.categoryId}</div>
              <h2 className="text-lg font-bold text-white">{category.name}</h2>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#1F1F1F] rounded-lg transition-colors">
            <X size={20} className="text-[#6B6B6B]" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Description */}
          <div>
            <h3 className="text-sm font-semibold text-white mb-2">Description</h3>
            <p className="text-sm text-[#6B6B6B]">{category.description}</p>
            <div className="flex items-center gap-4 mt-3 text-xs">
              <span className="text-[#6B6B6B]">
                Typical: <span className="text-white">{category.typicalPercentage || 'varies'}</span> of Scope 3
              </span>
              <span className={`px-2 py-0.5 rounded ${category.upstream ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'}`}>
                {category.upstream ? 'Upstream' : 'Downstream'}
              </span>
            </div>
          </div>

          {/* Frameworks */}
          {category.frameworks?.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-white mb-2">Satisfies Frameworks</h3>
              <div className="flex flex-wrap gap-2">
                {category.frameworks.map((fw) => (
                  <Badge key={fw} className="bg-teal-500/20 text-teal-400 border-0">
                    {fw}
                  </Badge>
                ))}
              </div>
              <p className="text-xs text-emerald-400 mt-2">
                🔗 Superpower #1: Collect once, satisfy {category.frameworks.length} frameworks
              </p>
            </div>
          )}

          {/* Collection Methods */}
          <div>
            <h3 className="text-sm font-semibold text-white mb-2">Recommended Collection Methods</h3>
            <div className="grid grid-cols-2 gap-3">
              {applicableMethods.map((method) => {
                const MethodIcon = COLLECTION_METHODS[method.methodId]?.icon || Database;
                return (
                  <div key={method.methodId} className="bg-[#111111] border border-[#1F1F1F] rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <MethodIcon size={14} className="text-emerald-400" />
                      <span className="text-sm font-medium text-white">{method.name}</span>
                    </div>
                    <p className="text-xs text-[#6B6B6B] mb-2">{method.description}</p>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-[#6B6B6B]">Quality: <span className="text-white">{method.dataQualityRange?.join('-')}/5</span></span>
                      <span className="text-[#6B6B6B]">{method.estimatedTime}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Projects */}
          {projects.length > 0 ? (
            <div>
              <h3 className="text-sm font-semibold text-white mb-2">Active Projects</h3>
              <div className="space-y-2">
                {projects.map((p) => (
                  <div key={p.projectId} className="bg-[#111111] border border-[#1F1F1F] rounded-lg p-3 flex items-center justify-between">
                    <div>
                      <div className="text-xs font-mono text-emerald-400">{p.projectId}</div>
                      <div className="text-sm text-white">{p.name}</div>
                    </div>
                    <Badge className="bg-amber-500/20 text-amber-400 border-0 text-[10px]">
                      {p.stage}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-[#111111] border border-dashed border-[#2F2F2F] rounded-lg p-6 text-center">
              <AlertCircle className="text-[#3F3F3F] mx-auto mb-2" size={24} />
              <p className="text-sm text-[#6B6B6B]">No projects for this category yet</p>
              <p className="text-xs text-[#3F3F3F] mt-1">Create a project to start collecting data</p>
            </div>
          )}

          {/* Create Project Form */}
          {showCreateForm ? (
            <div className="bg-[#111111] border border-emerald-500/30 rounded-lg p-4 space-y-3">
              <h3 className="text-sm font-semibold text-white">Create New Project</h3>
              <input
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#2F2F2F] rounded-lg text-white text-sm focus:border-emerald-500 focus:outline-none"
                placeholder="Project name"
              />
              <div className="flex items-center gap-2">
                <Button onClick={handleCreate} className="bg-emerald-500 hover:bg-emerald-600 text-black">
                  Create Project
                </Button>
                <Button variant="outline" onClick={() => setShowCreateForm(false)} className="border-[#2F2F2F]">
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button 
              onClick={() => setShowCreateForm(true)} 
              className="w-full bg-emerald-500 hover:bg-emerald-600 text-black"
            >
              <Plus size={16} className="mr-2" />
              Start Data Collection
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// COMPONENT: Create Project Modal
// ============================================================================
function CreateProjectModal({ categories, onClose, onCreateProject }) {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [projectName, setProjectName] = useState('');

  const handleCreate = () => {
    if (selectedCategory && projectName.trim()) {
      onCreateProject(selectedCategory, projectName);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[#0A0A0A] border border-[#1F1F1F] rounded-2xl max-w-xl w-full">
        <div className="flex items-center justify-between p-6 border-b border-[#1F1F1F]">
          <h2 className="text-lg font-bold text-white">Create Scope 3 Project</h2>
          <button onClick={onClose} className="p-2 hover:bg-[#1F1F1F] rounded-lg">
            <X size={20} className="text-[#6B6B6B]" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Category Selection */}
          <div>
            <label className="text-sm font-medium text-white block mb-2">Select Category</label>
            <div className="grid grid-cols-3 gap-2 max-h-60 overflow-y-auto">
              {categories.map((cat) => {
                const Icon = CATEGORY_ICONS[cat.categoryId] || Package;
                const isSelected = selectedCategory === cat.categoryId;
                return (
                  <button
                    key={cat.categoryId}
                    onClick={() => {
                      setSelectedCategory(cat.categoryId);
                      setProjectName(`Cat ${cat.categoryId}: ${cat.name} - 2025`);
                    }}
                    className={`p-3 rounded-lg text-left transition-all ${
                      isSelected 
                        ? 'bg-emerald-500/20 border-emerald-500/50' 
                        : 'bg-[#111111] border-[#1F1F1F] hover:border-[#2F2F2F]'
                    } border`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Icon size={14} className={isSelected ? 'text-emerald-400' : 'text-[#6B6B6B]'} />
                      <span className="text-xs font-mono text-emerald-400">Cat {cat.categoryId}</span>
                    </div>
                    <div className="text-xs text-white line-clamp-2">{cat.name}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Project Name */}
          <div>
            <label className="text-sm font-medium text-white block mb-2">Project Name</label>
            <input
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              className="w-full px-3 py-2 bg-[#111111] border border-[#2F2F2F] rounded-lg text-white text-sm focus:border-emerald-500 focus:outline-none"
              placeholder="Enter project name"
            />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3 pt-2">
            <Button 
              onClick={handleCreate} 
              disabled={!selectedCategory || !projectName.trim()}
              className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-black disabled:opacity-50"
            >
              Create Project
            </Button>
            <Button variant="outline" onClick={onClose} className="border-[#2F2F2F]">
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================
function formatNumber(num) {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(0)}K`;
  return num?.toFixed(0) || '0';
}
